import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Typography } from '@mui/material';
import { FirebaseContext } from 'common';
import { useTranslation } from 'react-i18next';

const PROFILE_URL = '/api/billing/profile';
const SETUP_URL = '/api/billing/setup-intent';

const loadStripeJs = () => new Promise((resolve, reject) => {
  if (window.Stripe) return resolve(window.Stripe);
  const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.Stripe), { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://js.stripe.com/v3/';
  script.async = true;
  script.onload = () => resolve(window.Stripe);
  script.onerror = reject;
  document.head.appendChild(script);
});

const CardBrand = ({ brand }) => {
  const value = String(brand || 'CARD').toUpperCase();
  if (value === 'MASTERCARD') {
    return <Box sx={{ width: 44, height: 32, borderRadius: 1.5, bgcolor: '#171717', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#eb001b', mr: -0.5 }} />
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#f79e1b', ml: -0.5, opacity: 0.95 }} />
    </Box>;
  }
  return <Box sx={{ width: 44, height: 32, borderRadius: 1.5, bgcolor: value === 'VISA' ? '#1434cb' : '#202124', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: value.length > 5 ? 9 : 12 }}>
    {value}
  </Box>;
};

const SavedCardPanel = () => {
  const { authRef } = useContext(FirebaseContext);
  const { i18n } = useTranslation();
  const fr = String(i18n.resolvedLanguage || i18n.language || '').startsWith('fr');
  const mountRef = useRef(null);
  const elementsRef = useRef(null);
  const stripeRef = useRef(null);
  const setupIntentIdRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState('');
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);

  const friendlySetupError = (code) => {
    const messages = {
      AUTHENTICATION_REQUIRED: fr ? 'Votre session a expiré. Reconnectez-vous puis réessayez.' : 'Your session expired. Sign in again and retry.',
      AUTHENTICATION_FAILED: fr ? 'Votre session a expiré. Reconnectez-vous puis réessayez.' : 'Your session expired. Sign in again and retry.',
      PAYMENT_PROVIDER_NOT_CONFIGURED: fr ? 'Le paiement par carte est temporairement indisponible.' : 'Card payment is temporarily unavailable.',
      STRIPE_PUBLIC_KEY_NOT_CONFIGURED: fr ? 'Le formulaire de carte est temporairement indisponible.' : 'The card form is temporarily unavailable.',
      STRIPE_SETUP_FAILED: fr ? 'Stripe n’a pas pu préparer la carte. Réessayez dans un instant.' : 'Stripe could not prepare the card. Please try again shortly.',
      PAYMENT_SETUP_FAILED: fr ? 'Le service de carte a rencontré un problème. Réessayez dans un instant.' : 'The card service encountered a problem. Please try again shortly.',
      CUSTOMER_REQUIRED: fr ? 'Ce moyen de paiement est réservé aux comptes clients.' : 'This payment method is available to customer accounts only.',
      PAYMENT_MANDATE_REQUIRED: fr ? 'Veuillez accepter l’autorisation pour continuer.' : 'Please accept the authorization to continue.',
    };
    return messages[code] || (fr ? 'Impossible d’ouvrir le formulaire sécurisé. Réessayez.' : 'Unable to open the secure form. Please retry.');
  };

  const authorizedFetch = async (url, options = {}) => {
    const user = authRef().currentUser;
    if (!user) throw new Error('AUTHENTICATION_REQUIRED');
    const token = await user.getIdToken();
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
  };

  const loadProfile = async () => {
    const response = await authorizedFetch(PROFILE_URL);
    if (!response.ok) throw new Error('PROFILE_LOAD_FAILED');
    const payload = await response.json();
    setProfile(payload.billingProfile);
    return payload.billingProfile;
  };

  const prepareCard = async () => {
    setView('add');
    setPhase('loading');
    setMessage('');
    try {
      const response = await authorizedFetch(SETUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'prepare', consentAccepted: false, requestId: `web_${Date.now()}` }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'SETUP_FAILED');
      const Stripe = await loadStripeJs();
      if (!Stripe) throw new Error('STRIPE_JS_UNAVAILABLE');
      stripeRef.current = Stripe(payload.publishableKey);
      elementsRef.current = stripeRef.current.elements({ clientSecret: payload.clientSecret });
      setupIntentIdRef.current = payload.setupIntentId;
      if (mountRef.current) mountRef.current.replaceChildren();
      elementsRef.current.create('payment', { layout: 'tabs' }).mount(mountRef.current);
      setPhase('ready');
    } catch (error) {
      setPhase('idle');
      setMessage(friendlySetupError(error.message));
    }
  };

  useEffect(() => {
    loadProfile()
      .then((billingProfile) => {
        const methods = (billingProfile && billingProfile.paymentMethods) || [];
        if (!methods.length && !(billingProfile && billingProfile.last4)) {
          prepareCard();
        }
      })
      .catch(() => setMessage(fr ? 'Impossible de charger le moyen de paiement.' : 'Unable to load payment method.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConsent = (event) => {
    const accepted = event.target.checked;
    setConsent(accepted);
    setMessage('');
  };

  const confirmCard = async () => {
    if (!consent) {
      setMessage(fr ? 'Acceptez l’autorisation pour enregistrer la carte.' : 'Accept the authorization to save the card.');
      return;
    }
    setPhase('saving');
    setMessage('');
    const mandateResponse = await authorizedFetch(SETUP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'acceptMandate',
        consentAccepted: true,
        setupIntentId: setupIntentIdRef.current,
      }),
    });
    const mandatePayload = await mandateResponse.json().catch(() => ({}));
    if (!mandateResponse.ok) {
      setPhase('ready');
      setMessage(friendlySetupError(mandatePayload.error));
      return;
    }
    const result = await stripeRef.current.confirmSetup({
      elements: elementsRef.current,
      confirmParams: { return_url: `${window.location.origin}/profile` },
      redirect: 'if_required',
    });
    if (result.error) {
      setPhase('ready');
      setMessage(result.error.message || (fr ? 'Carte refusée.' : 'Card declined.'));
      return;
    }
    setPhase('complete');
    setMessage(fr ? 'Carte enregistrée avec succès.' : 'Card saved successfully.');
    for (const delay of [800, 1800, 3500, 6000, 9000]) {
      setTimeout(() => loadProfile().then((billingProfile) => {
        if (billingProfile && billingProfile.last4) {
          setView('list');
          setMessage('');
        }
      }).catch(() => {}), delay);
    }
  };

  const methods = profile && profile.paymentMethods && profile.paymentMethods.length
    ? profile.paymentMethods
    : (profile && profile.last4 ? [{
      id: 'legacy-default', type: 'CARD', brand: profile.brand,
      last4: profile.last4, isDefault: true,
    }] : []);

  const chooseDefault = async (method) => {
    if (method.id === 'legacy-default' || method.isDefault) return;
    setPhase('saving');
    setMessage('');
    try {
      const response = await authorizedFetch(SETUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setDefault', paymentMethodId: method.id }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'SET_DEFAULT_FAILED');
      await loadProfile();
      setSelected({ ...method, isDefault: true });
      setMessage(fr ? 'Moyen de paiement par défaut mis à jour.' : 'Default payment method updated.');
      setPhase('complete');
    } catch (error) {
      setPhase('idle');
      setMessage(friendlySetupError(error.message));
    }
  };

  const deleteCard = async (method) => {
    const confirmed = window.confirm(fr
      ? `Supprimer la carte se terminant par ${method.last4} ?`
      : `Delete the card ending in ${method.last4}?`);
    if (!confirmed) return;
    setPhase('saving');
    setMessage('');
    try {
      const response = await authorizedFetch(SETUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', paymentMethodId: method.id }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'DELETE_PAYMENT_METHOD_FAILED');
      await loadProfile();
      setSelected(null);
      setView('list');
      setPhase('complete');
      setMessage(fr ? 'Carte supprimée.' : 'Card deleted.');
    } catch (error) {
      setPhase('idle');
      setMessage(friendlySetupError(error.message));
    }
  };

  return <Paper elevation={0} sx={{ mt: 3, p: { xs: 2.25, sm: 3 }, width: '100%', borderRadius: 4, border: '1px solid #e6e9ef' }}>
    {view === 'list' && <>
      <Typography variant="h6" fontWeight={700}>{fr ? 'Modes de paiement' : 'Payment methods'}</Typography>
      <Box sx={{ mt: 1.5 }}>
        {methods.map((method) => <Box key={method.id}
          role="button" tabIndex={0}
          onClick={() => { setSelected(method); setView('detail'); setMessage(''); }}
          sx={{ py: 1.6, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', borderBottom: '1px solid #edf0f3' }}>
          <CardBrand brand={method.brand} />
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600}>{`${method.type === 'GOOGLE_PAY' ? 'Google Pay · ' : ''}${String(method.brand || 'Card')} •••• ${method.last4}`}</Typography>
            {method.isDefault && <Typography variant="caption" color="primary">{fr ? 'Par défaut' : 'Default'}</Typography>}
          </Box>
          <Typography color="text.secondary" sx={{ fontSize: 28 }}>›</Typography>
        </Box>)}
      </Box>
      <Button variant="contained" onClick={prepareCard} sx={{ mt: 2.5, borderRadius: 8, bgcolor: '#161616', '&:hover': { bgcolor: '#000' } }}>
        + {fr ? 'Ajouter un moyen de paiement' : 'Add payment method'}
      </Button>
    </>}
    {view === 'detail' && selected && <>
      <Button onClick={() => { setView('list'); setMessage(''); }} sx={{ minWidth: 0, px: 0, color: 'text.primary' }}>← {fr ? 'Retour' : 'Back'}</Button>
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>{selected.type === 'GOOGLE_PAY' ? 'Google Pay' : String(selected.brand || 'Carte')}</Typography>
          <Typography variant="h6" color="text.secondary">•••• {selected.last4}</Typography>
        </Box>
        <CardBrand brand={selected.brand} />
      </Box>
      {selected.expMonth && selected.expYear && <Box sx={{ mt: 4 }}>
        <Typography color="text.secondary">{fr ? "Date d’expiration" : 'Expiration date'}</Typography>
        <Typography variant="h6">{String(selected.expMonth).padStart(2, '0')} / {selected.expYear}</Typography>
      </Box>}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #edf0f3' }}>
        {selected.isDefault ? <Typography fontWeight={700}>✓ {fr ? 'Moyen de paiement par défaut' : 'Default payment method'}</Typography>
          : <Button variant="outlined" disabled={phase === 'saving'} onClick={() => chooseDefault(selected)} sx={{ borderRadius: 8 }}>
            {fr ? 'Définir par défaut' : 'Set as default'}
          </Button>}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" disabled={phase === 'saving'} onClick={prepareCard} sx={{ borderRadius: 8 }}>
            {fr ? 'Modifier ou remplacer' : 'Edit or replace'}
          </Button>
          <Button color="error" disabled={phase === 'saving' || selected.id === 'legacy-default'} onClick={() => deleteCard(selected)} sx={{ borderRadius: 8 }}>
            {fr ? 'Supprimer la carte' : 'Delete card'}
          </Button>
        </Box>
      </Box>
      {message && <Alert severity={phase === 'complete' ? 'success' : 'error'} sx={{ mt: 2 }}>{message}</Alert>}
    </>}
    {view === 'add' && <>
    <Button onClick={() => { setView('list'); setMessage(''); }} sx={{ minWidth: 0, px: 0, color: 'text.primary' }}>← {fr ? 'Retour' : 'Back'}</Button>
    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>{fr ? 'Ajouter un moyen de paiement' : 'Add payment method'}</Typography>
    {phase === 'loading' && <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 2 }}>
      <CircularProgress size={22} />
      <Typography color="text.secondary">{fr ? 'Chargement…' : 'Loading…'}</Typography>
    </Box>}
    <Box ref={mountRef} sx={{ mt: phase === 'ready' || phase === 'saving' ? 3 : 0 }} />
    {(phase === 'ready' || phase === 'saving') && <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {fr ? 'Aucun débit maintenant. Paiement sécurisé par Stripe.' : 'No charge now. Secured by Stripe.'}
      </Typography>
      <FormControlLabel sx={{ mt: 1.5, alignItems: 'flex-start' }} control={<Checkbox checked={consent} disabled={phase === 'saving'} onChange={handleConsent} />}
        label={fr ? "J’autorise l’enregistrement de cette carte pour mes courses." : 'I authorize saving this card for my rides.'} />
    </>}
    {(phase === 'ready' || phase === 'saving') && <Button fullWidth variant="contained" disabled={phase === 'saving'} onClick={confirmCard} sx={{ mt: 2, borderRadius: 8 }}>
      {phase === 'saving' ? (fr ? 'Enregistrement…' : 'Saving…') : (fr ? 'Enregistrer la carte' : 'Save card')}
    </Button>}
    {phase === 'idle' && <Button fullWidth variant="outlined" onClick={prepareCard} sx={{ mt: 2, borderRadius: 8 }}>
      {fr ? 'Réessayer' : 'Retry'}
    </Button>}
    {message && <Alert severity={phase === 'complete' ? 'success' : 'error'} sx={{ mt: 2 }}>{message}</Alert>}
    </>}
  </Paper>;
};

export default SavedCardPanel;
